import { Button, IconButton, Tag } from '@ads/components-react';
import {
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/ui/atoms';

import { useQuestionHistory } from '../../../hooks/useQuestionHistory';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Props do QuestionHistoryModal */
export interface QuestionHistoryModalProps {
  /** ID da questão cujo histórico será exibido, ou null quando o modal deve estar fechado */
  readonly questionId: string | null;
  /** Callback ao fechar o modal */
  readonly onClose: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_TAG: Record<
  number,
  { status: 'positive' | 'helper' | 'negative' | 'default'; label: string }
> = {
  1: { status: 'helper', label: 'Pendente' },
  2: { status: 'positive', label: 'Aprovado' },
  3: { status: 'negative', label: 'Rejeitado' },
  4: { status: 'default', label: 'Em Revisão' },
};

function formatDate(dateStr: string) {
  if (!dateStr) return '—';
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  } catch {
    return '—';
  }
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function HistorySkeleton() {
  return (
    <div className="flex flex-col gap-md p-lg">
      {/* Approval section skeleton */}
      <div className="flex flex-col gap-sm rounded-sm border-hairline border-ctx-layout-border p-mini">
        <Skeleton className="h-micro w-32" />
        <div className="flex items-center gap-sm">
          <Skeleton className="h-xxs w-20 rounded-pill" />
          <Skeleton className="h-micro w-48" />
        </div>
      </div>

      {/* Exam list skeleton */}
      <div className="flex flex-col gap-nano">
        <Skeleton className="h-micro w-40" />
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="grid grid-cols-4 gap-xs py-xs">
            <Skeleton className="col-span-2 h-micro" />
            <Skeleton className="h-micro" />
            <Skeleton className="h-micro w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Modal de histórico de uma questão da Biblioteca de Questões.
 *
 * Exibe:
 * - Status de aprovação atual (com justificativa quando rejeitada)
 * - Lista paginada de provas que utilizam esta questão
 *
 * Usa `useQuestionHistory` que combina `/approve-editor` e `/exam-list`.
 *
 * @example
 * ```tsx
 * <QuestionHistoryModal questionId={selectedId} onClose={() => setSelectedId(null)} />
 * ```
 */
export function QuestionHistoryModal({ questionId, onClose }: QuestionHistoryModalProps) {
  const { t } = useTranslation('questionLibrary');
  const { exams, approval, isLoading, totalExams } = useQuestionHistory(questionId);

  const isOpen = questionId !== null;
  const statusInfo = approval
    ? (STATUS_TAG[approval.status] ?? {
        status: 'default' as const,
        label: approval.statusDescription,
      })
    : null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />

        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-lg py-mini">
            <Dialog.Title className="text-ctx-content-title ts-heading-sm">
              {t('historyModal.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('historyModal.close')}
              />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="at-scrollbar flex-1 overflow-y-auto">
            {isLoading ? (
              <HistorySkeleton />
            ) : (
              <div className="flex flex-col gap-xs p-xs">
                {/* Seção: Status de Aprovação */}
                {approval && (
                  <section className="flex flex-col gap-xs">
                    <h3 className="text-ctx-content-title ts-subtitle-sm">
                      {t('historyModal.approvalSection')}
                    </h3>

                    <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface">
                      <div className="flex items-center gap-sm px-mini py-xs">
                        <span className="shrink-0 text-ctx-content-placeholder ts-caption-xxs">
                          Status
                        </span>
                        {statusInfo && (
                          <Tag
                            label={statusInfo.label || approval.statusDescription}
                            status={statusInfo.status}
                            size="md"
                            custom={{ text: 'text-nowrap' }}
                          />
                        )}
                      </div>

                      {(approval.statusJustification || approval.status === 3) && (
                        <div className="border-t-hairline border-ctx-layout-border px-mini py-xs">
                          <span className="mb-quark block text-ctx-content-placeholder ts-caption-xxs">
                            {t('historyModal.justification')}
                          </span>
                          <p className="text-ctx-content-base ts-paragraph-xs">
                            {approval.statusJustification || t('historyModal.noJustification')}
                          </p>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Seção: Provas */}
                <section className="flex flex-col gap-xs">
                  <div className="flex items-center justify-between">
                    <h3 className="text-ctx-content-title ts-subtitle-sm">
                      {t('historyModal.examsSection')}
                    </h3>
                    {totalExams > 0 && (
                      <span className="text-ctx-content-placeholder ts-caption-xxs">
                        {totalExams} {totalExams === 1 ? 'prova' : 'provas'}
                      </span>
                    )}
                  </div>

                  {exams.length === 0 ? (
                    <div className="flex flex-col items-center gap-sm rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body p-lg text-center">
                      <ClipboardDocumentListIcon className="size-md text-ctx-content-placeholder" />
                      <p className="text-ctx-content-placeholder ts-paragraph-xs">
                        {t('historyModal.noExams')}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border">
                      {/* Cabeçalho */}
                      <div className="grid grid-cols-12 gap-xs bg-ctx-layout-body px-mini py-nano">
                        <span className="col-span-4 text-ctx-content-title ts-subtitle-xxs">
                          {t('historyModal.columns.title')}
                        </span>
                        <span className="col-span-3 text-ctx-content-title ts-subtitle-xxs">
                          {t('historyModal.columns.discipline')}
                        </span>
                        <span className="col-span-2 text-ctx-content-title ts-subtitle-xxs">
                          {t('historyModal.columns.type')}
                        </span>
                        <span className="col-span-1 text-center text-ctx-content-title ts-subtitle-xxs">
                          {t('historyModal.columns.active')}
                        </span>
                        <span className="col-span-2 text-ctx-content-title ts-subtitle-xxs">
                          {t('historyModal.columns.createdAt')}
                        </span>
                      </div>

                      {/* Linhas */}
                      <div className="divide-y divide-ctx-layout-border">
                        {exams.map(exam => (
                          <div
                            key={exam.id}
                            className="grid grid-cols-12 items-center gap-xs px-mini py-xs hover:bg-ctx-layout-body transition-colors"
                          >
                            <span className="col-span-4 line-clamp-2 text-ctx-content-title ts-paragraph-xxs">
                              {exam.title || '—'}
                            </span>
                            <span className="col-span-3 line-clamp-1 text-ctx-content-base ts-paragraph-xxs">
                              {exam.discipline || '—'}
                            </span>
                            <span className="col-span-2 text-ctx-content-base ts-paragraph-xxs">
                              {exam.examType || '—'}
                            </span>
                            <div className="col-span-1 flex justify-center">
                              {exam.isActive ? (
                                <CheckCircleIcon
                                  className="size-xxs text-base-success-500"
                                  aria-label={t('table.aria.active')}
                                />
                              ) : (
                                <XCircleIcon
                                  className="size-xxs text-base-danger-500"
                                  aria-label={t('table.aria.inactive')}
                                />
                              )}
                            </div>
                            <span className="col-span-2 text-ctx-content-placeholder ts-caption-xxs">
                              {formatDate(exam.creationDate)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end border-t-hairline border-ctx-layout-border px-lg py-xxs">
            <Dialog.Close asChild>
              <Button hierarchy="secondary" size="md">
                {t('historyModal.close')}
              </Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
